import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';

export type RetroMode = 'today' | 'days';
export type RetroApiValue = number | null; // null=off, 0=today, >=1=days

/**
 * Encapsulates the form handling for "Retroactive Edit Limit" (lock past days) setting.
 * - UI-Form: enabled + mode + days
 * - Model: null | number (null=disabled, 0=today, 1+=days back)
 */
export class RetroactiveEditLimitFormFacade {
    readonly form: FormGroup<{
        enabled: FormControl<boolean>;
        mode: FormControl<RetroMode>;
        days: FormControl<number | null>;
    }>;
    private readonly destroy$ = new Subject<void>();

    constructor(private fb: FormBuilder) {
        this.form = this.fb.group({
            enabled: this.fb.nonNullable.control(false),
            mode: this.fb.nonNullable.control<RetroMode>('today'),
            days: this.fb.control<number | null>(
                { value: 1, disabled: true },
                { validators: [Validators.min(1), Validators.max(365)] },
            ),
        });

        this.wireUp();
    }

    /** Call this from component ngOnDestroy */
    destroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** Sets the form values by the model */
    setValue(value: RetroApiValue): void {
        if (value == null) {
            this.form.patchValue({ enabled: false, mode: 'today', days: 1 }, { emitEvent: true });
            return;
        }

        if (value === 0) {
            this.form.patchValue({ enabled: true, mode: 'today', days: 1 }, { emitEvent: true });
            return;
        }

        this.form.patchValue({ enabled: true, mode: 'days', days: value }, { emitEvent: true });
    }

    /** Gets the model value by the form values */
    getValue(): RetroApiValue {
        const enabled = this.form.controls.enabled.value;
        if (!enabled) return null;

        const mode = this.form.controls.mode.value;
        if (mode === 'today') return 0;

        const days = this.form.controls.days.value;
        return days ?? 1;
    }

    /**
     * Returns true if the setting (toggle-switch) is enabled.
     */
    public get isEnabled(): boolean {
        return this.form.controls.enabled.value;
    }

    private wireUp(): void {
        const enabledCtrl = this.form.controls.enabled;
        const modeCtrl = this.form.controls.mode;
        const daysCtrl = this.form.controls.days;

        enabledCtrl.valueChanges.pipe(startWith(enabledCtrl.value), takeUntil(this.destroy$)).subscribe((enabled) => {
            if (!enabled) {
                daysCtrl.disable({ emitEvent: false });
                modeCtrl.setValue('today', { emitEvent: false });
                return;
            }
            this.applyMode(modeCtrl.value);
        });

        modeCtrl.valueChanges.pipe(startWith(modeCtrl.value), takeUntil(this.destroy$)).subscribe((mode) => {
            if (!enabledCtrl.value) return;
            this.applyMode(mode);
        });
    }

    private applyMode(mode: RetroMode): void {
        const daysCtrl = this.form.controls.days;
        if (mode === 'days') {
            daysCtrl.enable({ emitEvent: false });
            const current = daysCtrl.value;
            if (current == null || Number.isNaN(current) || current < 1) {
                daysCtrl.setValue(1, { emitEvent: false });
            }
        } else {
            daysCtrl.disable({ emitEvent: false });
        }
    }
}
